import { useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import Main from "@/components/Main";
import WorkedExample from "@/components/WorkedExample";
import Seo from "@/components/Seo";
import {
  Hexagon,
  Lightbulb,
  Shuffle,
  Check,
  X as XIcon,
  RefreshCw,
  ArrowRight,
} from "@/lib/icons";
import {
  CHAIN_PREFIXES,
  FUNCTIONAL_GROUPS,
  ORG_CATEGORY_LABELS,
  ORG_PROBLEMS,
  type OrgCategory,
  type OrgDifficulty,
  type OrgProblem,
} from "@/data/organicPrimer";

const ALL_CATEGORIES = Object.keys(ORG_CATEGORY_LABELS) as OrgCategory[];
const ALL_DIFFICULTIES: OrgDifficulty[] = ["easy", "medium", "hard"];

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface AttemptState {
  problemId: string;
  selected: number | null;
  graded: boolean;
}

export default function OrganicPrimer() {
  const [enabledCategories, setEnabledCategories] = useState<Set<OrgCategory>>(
    new Set(ALL_CATEGORIES),
  );
  const [difficulty, setDifficulty] = useState<OrgDifficulty | "all">("all");

  const pool = useMemo(
    () =>
      ORG_PROBLEMS.filter(
        (p) =>
          enabledCategories.has(p.category) &&
          (difficulty === "all" || p.difficulty === difficulty),
      ),
    [enabledCategories, difficulty],
  );

  const [current, setCurrent] = useState<OrgProblem | null>(
    () => ORG_PROBLEMS[0] ?? null,
  );
  const [attempt, setAttempt] = useState<AttemptState>({
    problemId: ORG_PROBLEMS[0]?.id ?? "",
    selected: null,
    graded: false,
  });
  const [stats, setStats] = useState({ attempted: 0, correct: 0 });

  const next = () => {
    if (pool.length === 0) return;
    let p = pickRandom(pool);
    if (pool.length > 1 && current && p.id === current.id) {
      p = pickRandom(pool.filter((q) => q.id !== current.id));
    }
    setCurrent(p);
    setAttempt({ problemId: p.id, selected: null, graded: false });
  };

  const submit = () => {
    if (!current || attempt.selected === null) return;
    const ok = attempt.selected === current.correct;
    setAttempt((a) => ({ ...a, graded: true }));
    setStats((s) => ({
      attempted: s.attempted + 1,
      correct: s.correct + (ok ? 1 : 0),
    }));
  };

  const toggleCategory = (c: OrgCategory) => {
    setEnabledCategories((prev) => {
      const next = new Set(prev);
      if (next.has(c)) {
        if (next.size > 1) next.delete(c);
      } else next.add(c);
      return next;
    });
  };

  const accuracy =
    stats.attempted === 0
      ? 0
      : Math.round((stats.correct / stats.attempted) * 100);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Organic Chemistry Primer — ElementPad" description="Introduction to organic chemistry: functional groups, naming, isomers, and basic reactions for Grades 11-12." path="/learn/organic-primer" />
      <AppHeader />
      <Main className="max-w-5xl mx-auto px-4 py-8 space-y-10">
        {/* Header */}
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
            <Hexagon size={14} aria-hidden="true" />
            Conceptual unit
          </div>
          <h1 className="text-3xl font-bold text-foreground">Organic Chemistry Primer</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            One stop for the carbon-chain basics: functional groups, IUPAC
            naming, isomers, and reading skeletal structures. Built to bridge
            into AP Chem, biology, and biochemistry.
          </p>
        </header>

        {/* ── Lesson ──────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="lesson-heading"
          className="rounded-xl border border-border bg-tile-bg p-6 space-y-6"
        >
          <div className="flex items-center gap-2">
            <Lightbulb size={16} className="text-primary" aria-hidden="true" />
            <h2
              id="lesson-heading"
              className="text-lg font-semibold text-foreground"
            >
              The four foundations
            </h2>
          </div>

          <ol className="grid sm:grid-cols-2 gap-3 text-sm">
            {[
              {
                t: "1. Carbon makes 4 bonds",
                d: "Always. If a structure shows fewer, the rest are implied hydrogens.",
              },
              {
                t: "2. The chain length names the molecule",
                d: "Memorise prefixes meth- through dec- (1–10 carbons). They drive every IUPAC name.",
              },
              {
                t: "3. The suffix tells you the family",
                d: "-ane (single bonds), -ene (C=C), -yne (C≡C), -ol (alcohol), -al (aldehyde), -one (ketone), -oic acid.",
              },
              {
                t: "4. Functional groups dictate behaviour",
                d: "Two molecules can share a backbone but behave very differently because of their functional groups.",
              },
            ].map((s) => (
              <li
                key={s.t}
                className="rounded-lg border border-border bg-background/40 p-3"
              >
                <p className="font-semibold text-foreground text-sm">{s.t}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.d}</p>
              </li>
            ))}
          </ol>

          {/* Carbon chain prefixes */}
          <div className="rounded-lg border border-border bg-background/40 p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">
              Carbon-chain prefixes (memorise these)
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {CHAIN_PREFIXES.map((row) => (
                <div
                  key={row.n}
                  className="rounded-md border border-border bg-card p-2 text-center"
                >
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    C{row.n}
                  </p>
                  <p className="font-mono text-sm text-primary">{row.prefix}</p>
                  <p className="text-xs text-muted-foreground">{row.alkane}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Functional groups gallery */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">
              The 10 functional groups every student must know
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              {FUNCTIONAL_GROUPS.map((g) => (
                <article
                  key={g.name}
                  className="rounded-lg border border-border bg-background/40 p-4 space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-foreground">
                      {g.name}
                    </h4>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                      {g.nameMarker}
                    </span>
                  </div>
                  <p className="font-mono text-sm text-foreground">{g.formula}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="text-foreground">e.g.</span> {g.example}
                  </p>
                  <p className="text-xs text-muted-foreground">{g.whyItMatters}</p>
                </article>
              ))}
            </div>
          </div>

          <WorkedExample
            title="Naming CH₃CH(OH)CH₂CH₃"
            problem="Give the IUPAC name for CH₃CH(OH)CH₂CH₃."
            steps={[
              {
                label: "Step 1: Count the longest carbon chain",
                explain:
                  "Walk the carbons end to end. Here we have 4 carbons in a row → root prefix 'but-'.",
                formula: "C–C–C–C  →  but-",
              },
              {
                label: "Step 2: Identify the functional group",
                explain:
                  "There's an –OH group → alcohol → suffix -ol. The base name becomes butanol.",
                formula: "but- + -ol = butanol",
              },
              {
                label: "Step 3: Number the chain to give the OH the lowest locant",
                explain:
                  "Numbering from the LEFT puts OH on carbon 2. From the right also gives 2. Tie → pick left.",
                formula: "1: CH₃   2: CH(OH)   3: CH₂   4: CH₃",
              },
              {
                label: "Step 4: Assemble the name",
                explain:
                  "Locant + base + suffix, with hyphens between numbers and letters.",
                formula: "2-butanol",
              },
            ]}
            answer="2-butanol (a.k.a. sec-butyl alcohol)"
          />
        </section>

        {/* ── Drill ───────────────────────────────────────────────────────── */}
        <section aria-labelledby="drill-heading" className="space-y-4">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2
                id="drill-heading"
                className="text-lg font-semibold text-foreground"
              >
                Practice drill
              </h2>
              <p className="text-xs text-muted-foreground">
                Multiple choice, instant feedback with reasoning.
              </p>
            </div>
            <div
              className="text-xs font-mono text-muted-foreground"
              aria-live="polite"
            >
              <span className="text-foreground font-semibold">{stats.correct}</span>
              /{stats.attempted} correct
              {stats.attempted > 0 && (
                <span className="ml-2">({accuracy}% accuracy)</span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl border border-border bg-tile-bg p-4 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Difficulty
              </p>
              <div className="flex flex-wrap gap-2">
                {(["all", ...ALL_DIFFICULTIES] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setDifficulty(d)}
                    className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                      difficulty === d
                        ? "bg-foreground text-background"
                        : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Categories
              </p>
              <div className="flex flex-wrap gap-2">
                {ALL_CATEGORIES.map((c) => {
                  const on = enabledCategories.has(c);
                  return (
                    <button
                      key={c}
                      onClick={() => toggleCategory(c)}
                      aria-pressed={on}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                        on
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "bg-muted border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {ORG_CATEGORY_LABELS[c]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Drill card */}
          {current ? (
            <article className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-primary px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                  {ORG_CATEGORY_LABELS[current.category]} · {current.difficulty}
                </span>
                <button
                  onClick={next}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Shuffle size={12} aria-hidden="true" />
                  New problem
                </button>
              </div>

              <p className="text-base text-foreground leading-relaxed">
                {current.prompt}
              </p>

              {current.formula && (
                <div className="rounded-lg border border-border bg-background/40 p-3 font-mono text-sm text-foreground">
                  {current.formula}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-2">
                {current.options.map((opt, i) => {
                  const isSelected = attempt.selected === i;
                  const isCorrect = i === current.correct;
                  let cls =
                    "border-border bg-background/40 hover:bg-muted/60 hover:border-muted-foreground/40";
                  if (attempt.graded) {
                    if (isCorrect)
                      cls =
                        "border-emerald-500/40 bg-emerald-500/10 text-emerald-400";
                    else if (isSelected)
                      cls =
                        "border-destructive/40 bg-destructive/10 text-destructive";
                    else cls = "border-border bg-background/40 opacity-60";
                  } else if (isSelected) {
                    cls = "border-primary bg-primary/10";
                  }
                  return (
                    <button
                      key={i}
                      disabled={attempt.graded}
                      onClick={() =>
                        setAttempt((a) => ({ ...a, selected: i }))
                      }
                      className={`text-left rounded-lg border p-3 text-sm transition-colors ${cls}`}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        <span className="font-mono text-xs text-muted-foreground">
                          {String.fromCharCode(65 + i)}.
                        </span>
                        <span className="text-foreground flex-1">{opt}</span>
                        {attempt.graded && isCorrect && (
                          <Check
                            size={14}
                            className="text-emerald-400"
                            aria-hidden="true"
                          />
                        )}
                        {attempt.graded && isSelected && !isCorrect && (
                          <XIcon
                            size={14}
                            className="text-destructive"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-2">
                {!attempt.graded ? (
                  <button
                    onClick={submit}
                    disabled={attempt.selected === null}
                    className="h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Check answer
                  </button>
                ) : (
                  <button
                    onClick={next}
                    className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                  >
                    Next problem
                    <ArrowRight size={14} aria-hidden="true" />
                  </button>
                )}
              </div>

              {attempt.graded && (
                <div
                  role="status"
                  className={`rounded-lg border p-3 text-sm ${
                    attempt.selected === current.correct
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-destructive/30 bg-destructive/5"
                  }`}
                >
                  <p
                    className={`font-medium ${
                      attempt.selected === current.correct
                        ? "text-emerald-400"
                        : "text-destructive"
                    }`}
                  >
                    {attempt.selected === current.correct
                      ? "Correct."
                      : `Correct answer: ${String.fromCharCode(65 + current.correct)}. ${current.options[current.correct]}`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {current.insight}
                  </p>
                </div>
              )}
            </article>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No problems match your filters.
              <button
                onClick={() => {
                  setEnabledCategories(new Set(ALL_CATEGORIES));
                  setDifficulty("all");
                }}
                className="ml-2 inline-flex items-center gap-1 text-primary hover:underline"
              >
                <RefreshCw size={12} aria-hidden="true" />
                Reset filters
              </button>
            </div>
          )}
        </section>
      </Main>
    </div>
  );
}